import React, {
    ReactElement, useEffect, useMemo, useState,
} from "react";
import {
    FieldTypedValue, FormField, FormFieldReplacementInput, FormFieldType,
} from "../../../__generated__/api";
import FieldTypeValueDisplay from "../../form-field/FieldTypeValueDisplay";
import FieldSelector, { SelectedGroup, useFlatFields } from "../../pickers/field-selector/FieldSelector";
import { ReplacementGroup } from "../../replacement-group";
import { ActionProps } from "./base";

const FORM_GROUP_ID = "urn:fq:form-field-group:request";

const FORM_FIELD_ID = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,field)";
const FORM_VALUE_ID = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,value)";

const ALLOWED_EDIT_SYSTEM_FIELDS = new Set([
    "urn:fq:form-field:subject",
    "urn:fq:form-field:description",
    "urn:fq:form-field:requester",
]);

export default function SetTicketFieldValueSettings(props: ActionProps): ReactElement {
    const fields = useFlatFields([], {
        shouldShowAllFields: true,
        shouldGroupSystem: true,
    });

    const requestGroup = props.input.find(group => group.id === FORM_GROUP_ID) as ReplacementGroup;

    const currentReplacements = props.values.firstElement?.replacement ?? [];
    const fieldIdInput = props.values.find(row => row.value.fieldId === FORM_FIELD_ID)?.value.string;
    const fieldValueInput = props.values.find(row => row.value.fieldId === FORM_VALUE_ID)?.value;

    const selected = fields.flatFields[fieldIdInput as string];
    const selectedField = selected ? {
        field: selected.field as FormField,
        group: selected.group,
    } as SelectedGroup : undefined;

    const [fieldValue, setFieldValue] = useState<FieldTypedValue>();

    const setFieldFieldValue = (
        field: FormField,
        value: FieldTypedValue,
        replacements: FormFieldReplacementInput[] | undefined,
    ) => {
        props.onChange({
            values: [
                {
                    fieldId: FORM_FIELD_ID,
                    type: FormFieldType.Text,
                    displayValue: field.name,
                    string: field.id,
                },
                {
                    ...value,
                    fieldId: FORM_VALUE_ID,
                },
            ],
            replacements,
        });
    };

    useEffect(() => {
        if (fieldValueInput) {
            setFieldValue(fieldValueInput);
        }
    }, [
        fieldValueInput?.boolean
        ?? fieldValueInput?.date
        ?? fieldValueInput?.displayValue
        ?? fieldValueInput?.fieldId
        ?? fieldValueInput?.number
         ?? fieldValueInput?.options
         ?? fieldValueInput?.string
         ?? fieldValueInput?.type,
    ]);

    useEffect(() => {
        if (selectedField?.field?.type && selectedField?.field?.type !== fieldValueInput?.type) {
            setFieldValue(undefined);
        }
    }, [selectedField?.field?.type, fieldValueInput?.type]);

    const inputGroup = useMemo(() => [{
        ...requestGroup,
        input: requestGroup.input?.map(row => (ALLOWED_EDIT_SYSTEM_FIELDS.has(row.id) ? {
            ...row,
            isSystemField: false,
        } : row)),
    }], [requestGroup]);

    return (
        <div className="">
            <div>
                <div className="">
                    Field
                </div>

                <div className="">
                    <FieldSelector
                        shouldShowAllFields
                        shouldGroupSystem
                        onChange={value => {
                            setFieldFieldValue(
                                value?.field as FormField,
                                fieldValueInput as FieldTypedValue,
                                currentReplacements as FormFieldReplacementInput[],
                            );
                        }}
                        value={selectedField}
                        input={inputGroup}
                    />
                </div>
                <small>
                    Select which field you want to modify
                </small>
            </div>
            {selectedField && (
                <div>
                    <div className="mt-3">
                        Value
                    </div>

                    <div className="">
                        <FieldTypeValueDisplay
                            className=""
                            field={selectedField?.field as FormField}
                            shouldShowFieldName={false}
                            value={fieldValue}
                            onChange={(value, replace) => {
                                setFieldFieldValue(selectedField.field as FormField, value, replace);
                            }}
                            replaceableInput={props.input}
                            shouldUseEditor={props.shouldUseEditor}
                        />
                    </div>
                    <small>
                        What do you want to set the value to?
                    </small>
                </div>
            )}

        </div>

    );
}
